/* @flow */
import { Record, Map } from 'immutable';
import createDebugger from 'debug';
import { scaleOrdinal } from 'd3-scale';
import { IO } from 'trustar/fp';

const debug = createDebugger('app:modules:enclave:types'); // eslint-disable-line no-unused-vars

export const ENCLAVES_ENDPOINT = '/services/enclaves';
export const ENCLAVE_TEMPLATES_ENDPOINT = '/services/enclave-templates';

export const NONE = 'NONE';
export const READ_ONLY = 'READ_ONLY';
export const READ_CREATE = 'READ_CREATE';
export const READ_WRITE = 'READ_WRITE';

export type AccessManagementType =
  'ALLOWED'
  | 'FORBIDDEN'
  | 'COMPANY';

export type RawOptions = {
  query?: string,
  sortBy?: string,
  sortOrder?: 'ASC' | 'DESC',
};

export type EnclaveObject = {
  id: number,
  name: string,
  description: string,
  iconUrl?: string,
  iconSvgUrl?: string,
  color?: string,
  read?: boolean,
  create?: boolean,
  update?: boolean,
  credentialsType?: string,
  maximumAccess?: string,
  templateId?: string,
  templateName?: string,
  externalUrl?: string,
  companyId?: string,
  ingestSource?: string,
  label?: string,
  accessManagementType: AccessManagementType,
};

export const Options = Record({
  query: '',
  sortBy: 'name',
  sortOrder: 'DESC',
});

/**
 * Should not be used directly. Use createEnclave since that ensures there's a
 * color prop
 */
export const _Enclave = Record({
  id: null,
  name: '',
  description: '',
  iconUrl: '',
  iconSvgUrl: '',
  color: '',
  read: false,
  create: false,
  update: false,
  credentialsType: '',
  maximumAccess: '',
  templateId: null,
  templateName: null,
  externalUrl: null,
  companyId: null,
  ingestSource: '',
  label: '',
  accessManagementType: null,
});

export type Enclave = typeof _Enclave;

/**
 * Interop with immutable and standard object. Preference is given to standard
 * object props. Defaults to 'gray'
 */
const safeGetId = x =>
  IO.of(x).map(x => x.id || x.get('id')).foldOr(err => {
    debug('Could not get enclave id', err);
    return '__';
  });

const enclaveColorMapper = scaleOrdinal([
  'orange',
  'teal',
  'green',
  'pink',
  'red',
  'light-purple',
  'gold',
  'focus-blue',
  'purple',
  'dark-green',
  'blue',
  'dark-brown',
]);

/**
 * Creates an enclave and assigns a color. Must pass an existing object with
 * an id attribute
 */
export const createEnclave = (x: Object | Map<string, *>) => {
  const map = Map(x)
    .update('color', c => c || enclaveColorMapper(safeGetId(x)));

  return _Enclave(map);
};

/**
 * NOTE: This is temporary until our back end is modified to treat community as
 * an enclave. Until then we just use this instance.
 */
export const COMMUNITY_ENCLAVE = createEnclave({
  id: 'COMMUNITY',
  name: 'Community',
});

export type EncType = 'OPEN' | 'INTERNAL' | 'CLOSED_CONCRETE' | 'COMMUNITY' | 'OTHER';

/**
 * We group enclaves for our users to improve UX. This is how we determine an
 * enclaves "group"
 *
 * Update: There used to be a lot more logic here, but now the backend is
 * passing `label` which is a nice flat way of determining the enclave group.
 * I've left this function intact in case we need to update this logic sometime
 * in the near future.
 */
export const getEnclaveGroup = (x: Enclave): EncType => {
  return x.label;
};

export const isPrivateEnclave = (x: Enclave) => {
  return getEnclaveGroup(x) === 'INTERNAL';
};



// WEBPACK FOOTER //
// ./src/app/modules/enclave/types.js